import { UseFormRegister } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MapPin, Link as LinkIcon, FileText, Banknote } from "lucide-react";
import ReviewLinkSelector from "@/components/ReviewLinkSelector";
import { PaymentFormData } from "@/types/PaymentFormData";

interface PaymentOptionsFormProps {
  register: UseFormRegister<PaymentFormData>;
  watchIncludeReviewLink: boolean;
  setWatchIncludeReviewLink: (value: boolean) => void;
  watchIncludeAddress: boolean;
  setWatchIncludeAddress: (value: boolean) => void;
  watchIncludePdf: boolean;
  setWatchIncludePdf: (value: boolean) => void;
  watchPaidWithCash: boolean;
  setWatchPaidWithCash: (value: boolean) => void;
  handleReviewLinkSelected: (url: string) => void;
}

const PaymentOptionsForm = ({
  register,
  watchIncludeReviewLink,
  setWatchIncludeReviewLink,
  watchIncludeAddress,
  setWatchIncludeAddress,
  watchIncludePdf,
  setWatchIncludePdf,
  watchPaidWithCash,
  setWatchPaidWithCash,
  handleReviewLinkSelected,
}: PaymentOptionsFormProps) => {
  return (
    <>
      {/* Address Toggle */}
      <div className="flex items-center space-x-2">
        <Switch id="includeAddress" checked={watchIncludeAddress} onCheckedChange={setWatchIncludeAddress} />
        <Label htmlFor="includeAddress" className="flex items-center">
          <MapPin className="h-4 w-4 mr-2" />
          Include Customer Address
        </Label>
      </div>
      
      {watchIncludeAddress && (
        <div className="space-y-4 p-4 border rounded-md bg-muted/20">
          <div className="space-y-2">
            <Label htmlFor="street">Street</Label>
            <Input id="street" placeholder="123 Main St" {...register("street")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" placeholder="London" {...register("city")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode</Label>
              <Input id="postcode" placeholder="SW1A 1AA" {...register("postcode")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Input id="country" placeholder="United Kingdom" {...register("country")} />
          </div>
        </div>
      )}

      {/* Send Review Link Toggle */}
      <div className="flex items-center space-x-2">
        <Switch 
          id="includeReviewLink" 
          checked={watchIncludeReviewLink} 
          onCheckedChange={setWatchIncludeReviewLink} 
        />
        <Label htmlFor="includeReviewLink" className="flex items-center">
          <LinkIcon className="h-4 w-4 mr-2" />
          Send Review Link
        </Label>
      </div>
      
      {watchIncludeReviewLink && (
        <div className="space-y-4 p-4 border rounded-md bg-muted/20">
          <ReviewLinkSelector 
            onLinkSelected={handleReviewLinkSelected}
            label="Select Review Platform"
            className="w-full"
          />
        </div>
      )}

      {/* Include PDF Invoice Toggle */}
      <div className="flex items-center space-x-2">
        <Switch
          id="includePdfPaymentRequest"
          checked={watchIncludePdf}
          onCheckedChange={setWatchIncludePdf}
        />
        <Label htmlFor="includePdfPaymentRequest" className="flex items-center">
          <FileText className="h-4 w-4 mr-2" />
          Include PDF Invoice
        </Label>
      </div>

      {/* Paid With Cash Toggle */}
      <div className="flex items-center space-x-2">
        <Switch
          id="paidWithCash"
          checked={watchPaidWithCash}
          onCheckedChange={setWatchPaidWithCash}
        />
        <Label htmlFor="paidWithCash" className="flex items-center">
          <Banknote className="h-4 w-4 mr-2" />
          Paid With Cash
        </Label>
      </div>
    </>
  );
};

export default PaymentOptionsForm;
